/**
 * Multi-brand / White-label configuration
 * 
 * The active brand is determined by the hostname.
 * Add new brands by extending the `brands` record.
 */

export interface BrandConfig {
  /** Internal key */
  id: string;
  /** Production domain used in embed scripts (no trailing slash) */
  widgetDomain: string;
  /** Full product name shown in navbar, sidebar header, etc. */
  name: string;
  /** Short name (used when space is tight) */
  shortName: string;
  /** Slogan shown in footer */
  slogan: string;
  /** Support email */
  supportEmail: string;
  /** Legal email */
  legalEmail: string;
  /** Logo imports – resolved at runtime via the context */
  logos: {
    /** White / dark-bg version for navbar & landing */
    navbarDark: string;
    /** Icon only (small) */
    icon: string;
    /** Logo for login page (can be full-color) */
    login: string;
    /** Logo for sidebar header */
    sidebar: string;
  };
  /** CSS custom-property overrides applied to :root when this brand is active.
   *  Keys are CSS variable names WITHOUT the leading `--`.
   *  Values are raw HSL channel strings, e.g. "193 100% 37%". */
  cssVars: Record<string, string>;
  /** Dark-mode CSS var overrides */
  cssVarsDark: Record<string, string>;
  /** Hard-coded hex colours used in the landing page gradient, hero, etc. */
  palette: {
    /** Primary accent hex */
    primary: string;
    /** Hover / secondary accent hex */
    primaryHover: string;
    /** Neon / highlight hex */
    neon: string;
    /** Deep background hex */
    deepBg: string;
    /** Deep blue accent hex */
    deepBlue: string;
    /** CTA / conversion accent hex (optional) */
    ctaColor?: string;
    /** MiPymes orange (optional) */
    orange?: string;
  };
  /** Landing page hero agent name */
  heroAgentName: string;
  /** Whether to show the "Meta Business Partners" badge */
  showMetaBadge: boolean;
  /** Favicon path (relative to public/) */
  favicon: string;
  /** Footer slogan */
  footerSlogan: string;
  /** Contact email */
  contactEmail: string;
  /** Notification badge icon path */
  notificationBadge: string;
  /** Microsoft Clarity project ID */
  clarityId: string;
  /** External CTA URL for the landing page (opens in new tab). When unset the CTA routes internally. */
  externalCtaUrl?: string;
  /** Scheduling URL for the demo CTAs ("Agendar demo" / "Solicitar demo"). Opens in a new tab. */
  scheduleDemoUrl?: string;
  /** WhatsApp number in E.164 without the leading '+' (e.g. "573005777393").
   *  When set, the floating WhatsApp contact widget renders on the landing. */
  whatsappWidgetNumber?: string;
  advisorWhatsappNumber?: string;
  /** Live-demo video URL shown in the demo modal. When unset the modal shows a placeholder. */
  demoVideoUrl?: string;
  voiceAssistants: boolean;
  assistantCopy: {
    label: string;
    greetingIntro: string;
  };
}

/* ─── Brand definitions ─── */

const etbBrand: BrandConfig = {
  id: "etb",
  widgetDomain: "https://agentevirtual.etb.com.co",
  name: "Agente Virtual ETB",
  shortName: "Agente Virtual",
  slogan: "Serás lo que creas",
  supportEmail: "soporte@etb.com.co",
  legalEmail: "legal@etb.com.co",
  logos: {
    navbarDark: "/assets/etb-logo-tag-white.webp",
    icon: "/assets/etb-icon.webp",
    login: "/assets/etb-logo.png",
    sidebar: "/assets/etb-icon.webp",
  },
  cssVars: {
    "primary": "193 100% 37%",
    "ring": "193 100% 37%",
    "sidebar-primary": "193 100% 37%",
    "sidebar-ring": "193 100% 37%",
  },
  cssVarsDark: {
    "primary": "193 100% 42%",
    "ring": "193 100% 42%",
    "sidebar-primary": "193 100% 42%",
    "sidebar-ring": "193 100% 42%",
    "background": "197 64% 7%",
  },
  palette: {
    primary: "#0092bc",
    primaryHover: "#004C8F",
    neon: "#00FFFF",
    deepBg: "#214780",
    deepBlue: "#214780",
    ctaColor: "#FF6D00",
    orange: "#FF6D00",
  },
  heroAgentName: "Agente ETB",
  showMetaBadge: true,
  favicon: "/etb-favicon.ico",
  footerSlogan: "Serás lo que creas",
  contactEmail: "ventas@etb.com.co",
  notificationBadge: "/assets/etb-icon.webp",
  clarityId: "vwt0fh4c91",
  externalCtaUrl: "https://etb.com/mypes2fibra/?utm_source=BannerWebNC1&utm_medium=organic&utm_campaign=agentevirtual&bnv=b6",
  demoVideoUrl: "https://www.youtube.com/embed/GPprT2bOYKg?autoplay=1",
  voiceAssistants: false,
  assistantCopy: {
    label: "Asistente",
    greetingIntro: "¡Hola! Soy tu asistente de configuración de Agente Virtual ETB. 🚀",
  },
};

const flowBrand: BrandConfig = {
  id: "flow",
  widgetDomain: "https://flow-chat.desarrollandoando.com",
  name: "Flow",
  shortName: "Flow",
  slogan: "Connect every conversation",
  supportEmail: "soporteflow@desarrollandoando.com",
  legalEmail: "soporteflow@desarrollandoando.com",
  logos: {
    navbarDark: "/brand/flow-logo.png",
    icon: "/icons/favicon-badge-96x96.png",
    login: "/icons/ms-icon-310x310.png",
    sidebar: "/icons/favicon-96x96.png",
  },
  // These beat the stylesheet: BrandProvider writes them inline on <html>. The
  // FlowRedesign kit's accent lives here, not only in index.css, or the brand
  // would keep painting over it.
  cssVars: {
    "primary": "243.4 75.4% 58.6%",
    "ring": "243.4 75.4% 58.6%",
    "sidebar-primary": "243.4 75.4% 58.6%",
    "sidebar-ring": "243.4 75.4% 58.6%",
  },
  cssVarsDark: {
    // One accent in both themes now. The old swap to light blue was there
    // because the previous violet (#655ba3) was illegible on black; the kit's
    // is brighter, and white on it measures 4.70:1 — over the AA floor for a
    // filled control.
    "primary": "243.4 75.4% 58.6%",
    "primary-foreground": "0 0% 100%",
    "ring": "243.4 75.4% 58.6%",
    "sidebar-primary": "243.4 75.4% 58.6%",
    "sidebar-ring": "243.4 75.4% 58.6%",
    "background": "0 0% 0%",
  },
  palette: {
    primary: "#4f46e5",
    primaryHover: "#4338ca",
    neon: "#8fcff2",
    deepBg: "#0D0A14",
    deepBlue: "#3162ab",
  },
  heroAgentName: "Flow AI",
  showMetaBadge: true,
  favicon: "/favicon.png",
  footerSlogan: "Connect every conversation",
  contactEmail: "ventas@useflow.co",
  notificationBadge: "/icons/favicon-badge-96x96.png",
  clarityId: "vn1xvsrw8x",
  scheduleDemoUrl: "https://crm.desarrollando.co/",
  whatsappWidgetNumber: "573005777393",
  advisorWhatsappNumber: "573026116960",
  voiceAssistants: true,
  assistantCopy: {
    label: "Flow",
    greetingIntro: "¡Hola! Soy Flow, tu consultor de configuración. 🚀",
  },
};

const americasBrand: BrandConfig = {
  id: "americas",
  widgetDomain: "https://flow-americasbps.desarrollandoando.com",
  name: "Americas BPS",
  shortName: "Americas",
  slogan: "Moviéndonos hacia la transformación",
  supportEmail: "soporteflow@desarrollandoando.com",
  legalEmail: "soporteflow@desarrollandoando.com",
  logos: {
    // navbarDark is the official white lockup; icon/login are placeholders pending official exports.
    navbarDark: "/assets/americas-logo-full-white.webp",
    icon: "/assets/americas-icon.svg",
    login: "/assets/americas-logo-full.svg",
    sidebar: "/assets/americas-logo-full.svg",
  },
  cssVars: {
    "primary": "196 58% 49%",
    "ring": "196 58% 49%",
    "sidebar-primary": "196 58% 49%",
    "sidebar-ring": "196 58% 49%",
  },
  cssVarsDark: {
    "primary": "196 58% 55%",
    "ring": "196 58% 55%",
    "sidebar-primary": "196 58% 55%",
    "sidebar-ring": "196 58% 55%",
    "background": "224 44% 8%",
  },
  palette: {
    primary: "#349FC6",
    primaryHover: "#32467F",
    neon: "#0FF2AA",
    deepBg: "#101832",
    deepBlue: "#32467F",
    ctaColor: "#8C1D82",
  },
  heroAgentName: "Agente Americas",
  showMetaBadge: true,
  favicon: "/assets/americas-icon.svg",
  footerSlogan: "Moviéndonos hacia la transformación",
  contactEmail: "soporteflow@desarrollandoando.com",
  notificationBadge: "/assets/americas-icon.svg",
  clarityId: "",
  voiceAssistants: true,
  assistantCopy: {
    label: "Asistente",
    greetingIntro: "¡Hola! Soy tu asistente de configuración de Americas BPS. 🚀",
  },
};

/* ─── Brand registry ─── */
const brands: Record<string, BrandConfig> = {
  etb: etbBrand,
  flow: flowBrand,
  americas: americasBrand,
};

/**
 * Resolve brand from hostname.
 * Checks if the hostname contains a known brand key.
 * Defaults to "flow" (your own product) if no match.
 */
export function resolveBrand(hostname: string = window.location.hostname): BrandConfig {
  const h = hostname.toLowerCase();

  // ETB-specific domains
  if (h.includes("etb") || h.includes("agentevirtual")) {
    return brands.etb;
  }

  // Americas BPS domains (flow-americasbps.desarrollandoando.com)
  if (h.includes("americas")) {
    return brands.americas;
  }

  // Flow-specific domains (flow-chat.desarrollandoando.com, localhost, etc.)
  return brands.flow;
}

export { brands };
